// NAT HOF Niches Data - Personalization Engine

export interface NicheFaq {
  q: string;
  a: string;
}

export interface NicheRoutineStep {
  name: string;
  sku: string;
}

export interface NicheRoutine {
  step1: NicheRoutineStep;
  step2: NicheRoutineStep;
  step3?: NicheRoutineStep;
}

export interface NicheHero {
  headline: string;
  subheadline: string;
  bullets: string[];
}

export interface NicheSeo {
  keywords: string[];
  metaTitle: string;
  metaDescription: string;
}

export interface NicheCtas {
  primary: string;
  secondary: string;
}

export interface Niche {
  slug: string;
  title: string;
  hero: NicheHero;
  storytelling: string;
  offers: string[];
  routine: NicheRoutine;
  products: string[];
  activesFocus: string[];
  ctas: NicheCtas;
  seo: NicheSeo;
  faq: NicheFaq[];
  warnings: string[];
  utmHints: string[];
}

export const niches: Niche[] = [
  {
    slug: "onco-care",
    title: "OncoCare — pele sensibilizada por rádio e quimio",
    hero: {
      headline: "Quando a pele fica sensível, o cuidado precisa virar abrigo.",
      subheadline: "Rotina de suporte para conforto, barreira e higiene suave — sem perfume e com alta tolerabilidade.",
      bullets: [
        "Foco em limpeza suave e hidratação funcional",
        "Rotina para pele fragilizada e ressecamento intenso",
        "Cuidado pontual para áreas críticas e fissuras"
      ]
    },
    storytelling: "Cuidado seguro e sem perfume para peles sensibilizadas por rádio e quimio. Menos estímulo, mais função — para que sua pele atravesse essa fase com dignidade e conforto.",
    offers: [
      "Rotina de conforto (3 passos) para pele muito sensibilizada",
      "Kit de suporte diário com foco em barreira cutânea",
      "SOS fissuras e áreas críticas"
    ],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Hidratação intensa", sku: "BCH01" },
      step3: { name: "Cuidado intensivo localizado", sku: "BCB01" }
    },
    products: ["BCS01", "BBH01", "BCB01", "BCH01"],
    activesFocus: ["Ácido Hialurônico", "Alantoína", "Aloe Vera", "Óleo de Rosa-Mosqueta", "Óleo de Girassol Ozonizado"],
    ctas: {
      primary: "Fazer consulta guiada (2 min)",
      secondary: "Montar rotina de conforto"
    },
    seo: {
      keywords: ["pele pós-quimio", "hidratante para radioterapia", "pele sensibilizada", "rotina sem perfume"],
      metaTitle: "OncoCare — Rotina NAT HOF para pele sensibilizada",
      metaDescription: "Rotina de suporte para conforto e barreira cutânea em pele sensibilizada. Limpeza suave, hidratação funcional e cuidado intensivo."
    },
    faq: [
      { q: "Essa rotina é indicada para pele muito sensibilizada?", a: "Ela foi desenhada para alta tolerabilidade e suporte à barreira cutânea, com linguagem de cuidado e conforto. Em caso de dúvida, faça teste em pequena área e fale com especialista." },
      { q: "Posso usar diariamente?", a: "Sim, como rotina de cuidado contínuo, ajustando a frequência conforme a resposta da pele." },
      { q: "Qual produto usar nas áreas mais críticas?", a: "O cuidado intensivo localizado pode ser priorizado em fissuras e pontos de desconforto." },
      { q: "O sabonete agride a pele?", a: "A proposta é limpeza suave e funcional, minimizando agressão e ressecamento." },
      { q: "Como montar uma rotina mínima?", a: "Você pode começar com limpeza suave + hidratação e acrescentar o intensivo conforme necessidade." }
    ],
    warnings: [
      "Consulta guiada não substitui avaliação médica.",
      "Faça teste em pequena área se sua pele estiver extremamente sensibilizada."
    ],
    utmHints: ["onco", "oncologia", "radioterapia", "quimioterapia", "onco-care"]
  },
  {
    slug: "barreira-melasma",
    title: "Manchas e melasma — primeiro reconstruir a barreira",
    hero: {
      headline: "Antes de clarear, eu reconstruo a pele.",
      subheadline: "Rotina de suporte para reduzir desconforto e fortalecer a barreira quando a pele reage a ácidos e irritações.",
      bullets: [
        "Foco em barreira e conforto cutâneo",
        "Limpeza suave para evitar efeito rebote",
        "Cuidado pontual para áreas reativas"
      ]
    },
    storytelling: "Manchas pedem estratégia — e pele sensível pede respeito. Eu começo pelo básico: reparar a barreira e reduzir estímulos que amplificam inflamação e reatividade.",
    offers: [
      "Rotina barreira-primeiro",
      "Suporte para pele que descama com ácidos",
      "Cuidado pontual em áreas irritadas"
    ],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Hidratação/barreira", sku: "BCH01" },
      step3: { name: "Intensivo localizado", sku: "BCB01" }
    },
    products: ["BCS01", "BCH01", "BCB01", "BBH01"],
    activesFocus: ["Óleo de Rosa-Mosqueta", "Manteiga de Karité", "Alantoína", "Ácido Hialurônico"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Reparar barreira agora" },
    seo: {
      keywords: ["melasma pele sensível", "barreira cutânea manchas", "descamação por ácido", "rotina suave"],
      metaTitle: "Barreira primeiro — rotina NAT HOF para pele sensível com manchas",
      metaDescription: "Rotina de suporte para pele reativa a ácidos: limpeza suave, hidratação funcional e cuidado localizado para conforto e barreira."
    },
    faq: [
      { q: "Essa rotina clareia manchas?", a: "A proposta aqui é suporte e reparo de barreira. Pele irritada tende a reagir pior; conforto e barreira são base para qualquer estratégia." },
      { q: "Posso usar junto com ácidos?", a: "Você pode usar como suporte de conforto. Ajuste conforme tolerância e, se possível, valide com profissional." },
      { q: "Minha pele descama e arde, o que priorizar?", a: "Priorize limpeza suave e hidratação funcional; use o cuidado intensivo localizado quando necessário." },
      { q: "Qual a rotina mínima?", a: "Limpeza suave + hidratação. O intensivo entra como SOS." },
      { q: "Serve para pele muito sensível?", a: "Sim, com abordagem prudente e teste em pequena área." }
    ],
    warnings: ["Consulta guiada não substitui avaliação médica."],
    utmHints: ["melasma", "manchas", "pos-inflamatoria", "acidos", "clareamento"]
  },
  {
    slug: "inverno-baixa-umidade",
    title: "Tempo seco e inverno — hidratação intensa e barreira",
    hero: {
      headline: "Quando o ar seca, eu fortaleço a barreira.",
      subheadline: "Rotina para reduzir ressecamento e desconforto em dias frios e de baixa umidade.",
      bullets: [
        "Hidratação funcional e prolongada",
        "Limpeza suave para não agravar xerose",
        "SOS para fissuras quando necessário"
      ]
    },
    storytelling: "No inverno, a pele conta outra história: repuxa, coça e racha. Eu transformo isso em rotina constante: limpar sem agredir e hidratar com intenção.",
    offers: [
      "Rotina de inverno 24h (suporte)",
      "Tamanho família para constância (1kg)",
      "SOS fissuras e rachaduras"
    ],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Hidratação intensa", sku: "BCH01" },
      step3: { name: "SOS fissuras", sku: "BCB01" }
    },
    products: ["BBH01", "BCS01", "BCB01"],
    activesFocus: ["Ácido Hialurônico", "Óleo de Girassol", "Alantoína", "Aloe Vera"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Rotina de inverno 24h" },
    seo: {
      keywords: ["pele ressecada no frio", "hidratação intensa", "coceira no inverno", "xerose"],
      metaTitle: "Inverno e baixa umidade — rotina NAT HOF para pele ressecada",
      metaDescription: "Rotina de suporte para dias secos: limpeza suave, hidratação intensa e cuidado SOS para fissuras."
    },
    faq: [
      { q: "Quanto tempo até sentir conforto?", a: "A resposta varia, mas a proposta é conforto progressivo com constância e ajuste de frequência." },
      { q: "Posso usar no corpo todo?", a: "Sim. Ajuste a quantidade conforme necessidade." },
      { q: "Tenho rachaduras, o que faço?", a: "Use o cuidado SOS localizado nas áreas mais críticas." },
      { q: "Qual a rotina mínima?", a: "Hidratação diária + limpeza suave." },
      { q: "Serve para pele sensível?", a: "Sim, com foco em tolerabilidade e barreira." }
    ],
    warnings: [],
    utmHints: ["inverno", "tempo seco", "baixa umidade", "frio", "rachaduras"]
  },
  {
    slug: "viagem-neve-altitude",
    title: "Viagens, avião, neve e altitude — proteção em trânsito",
    hero: {
      headline: "Em trânsito, eu não abandono minha barreira.",
      subheadline: "Rotina portátil para baixa umidade de avião, calefação e clima frio/altitude.",
      bullets: [
        "Portabilidade sem perder função",
        "Hidratação para dias de baixa umidade",
        "SOS áreas críticas durante a viagem"
      ]
    },
    storytelling: "Viajar muda o clima — e o clima muda a pele. Eu levo comigo um cuidado silencioso: limpar, hidratar e ter um SOS sempre à mão.",
    offers: [
      "Rotina portátil (viagem)",
      "Checklist pele no avião",
      "SOS para áreas críticas"
    ],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Hidratação portátil", sku: "BCH01" },
      step3: { name: "SOS localizado", sku: "BCB01" }
    },
    products: ["BCH01", "BCS01", "BCB01"],
    activesFocus: ["Ácido Hialurônico", "Óleo de Rosa-Mosqueta", "Óleo de Girassol Ozonizado"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Montar kit de viagem" },
    seo: {
      keywords: ["pele ressecada no avião", "hidratação viagem neve", "kit viagem pele sensível", "altitude pele seca"],
      metaTitle: "Kit viagem — rotina NAT HOF para frio, avião e altitude",
      metaDescription: "Rotina portátil para baixa umidade: limpeza suave, hidratação funcional e cuidado SOS para áreas críticas."
    },
    faq: [
      { q: "O que muda na pele no avião?", a: "Baixa umidade pode aumentar ressecamento e desconforto; a ideia é reforçar hidratação e barreira." },
      { q: "Como usar no frio intenso?", a: "Reforce a hidratação e use o SOS nas áreas mais expostas." },
      { q: "Qual produto levar se eu quiser só 2 itens?", a: "Hidratação portátil + limpeza suave." },
      { q: "Tenho fissuras durante viagens, o que fazer?", a: "Use o cuidado SOS localizado e mantenha hidratação constante." },
      { q: "Serve para pele sensível?", a: "Sim, com proposta de tolerabilidade." }
    ],
    warnings: [],
    utmHints: ["viagem", "aviao", "neve", "altitude", "travel", "inverno viagem"]
  },
  {
    slug: "maternidade-segura",
    title: "Gestação, pós-parto e amamentação — rotina suave",
    hero: {
      headline: "A pele muda. O cuidado também precisa mudar.",
      subheadline: "Rotina delicada, sem perfume, para conforto em fases de maior sensibilidade cutânea.",
      bullets: [
        "Hidratação funcional e suave",
        "Limpeza que respeita a barreira",
        "Cuidado pontual para áreas críticas"
      ]
    },
    storytelling: "Na maternidade, tudo muda — inclusive a pele. Eu cuido com gentileza e constância: menos estímulo, mais conforto, mais presença.",
    offers: [
      "Rotina suave para gestação e pós-parto",
      "Constância com hidratação funcional",
      "SOS áreas ressecadas"
    ],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Hidratação diária", sku: "BCH01" },
      step3: { name: "Cuidado pontual", sku: "BCB01" }
    },
    products: ["BCS01", "BBH01", "BCH01", "BCB01"],
    activesFocus: ["Ácido Hialurônico", "Aloe Vera", "Alantoína", "Óleo de Rosa-Mosqueta"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Rotina suave para essa fase" },
    seo: {
      keywords: ["hidratação gestante sem perfume", "pele sensível gravidez", "rotina suave pós-parto"],
      metaTitle: "Maternidade segura — rotina NAT HOF para pele sensível",
      metaDescription: "Rotina suave e funcional para conforto e barreira cutânea em fases de maior sensibilidade."
    },
    faq: [
      { q: "Posso usar diariamente?", a: "Sim, como rotina de cuidado contínuo e suave." },
      { q: "Se minha pele estiver mais reativa, o que faço?", a: "Priorize limpeza suave e hidratação, e use o cuidado pontual conforme necessidade." },
      { q: "Qual rotina mínima?", a: "Limpeza suave + hidratação diária." },
      { q: "O que fazer em áreas muito ressecadas?", a: "Aplicar cuidado pontual localizado e reforçar hidratação." },
      { q: "Esse quiz substitui orientação médica?", a: "Não. É uma triagem guiada de rotina de cuidado." }
    ],
    warnings: ["Consulta guiada não substitui avaliação médica."],
    utmHints: ["gestante", "gravidez", "puerperio", "amamentacao", "maternidade"]
  },
  {
    slug: "pele-jovem-sensivel",
    title: "Jovens e adolescentes — rotina minimalista para pele sensível",
    hero: {
      headline: "Menos perfume. Mais função. Mais calma.",
      subheadline: "Rotina minimalista e funcional para pele sensível e reativa no dia a dia.",
      bullets: [
        "Limpeza suave e sensação de frescor",
        "Hidratação localizada sem pesar",
        "Rotina simples, consistente"
      ]
    },
    storytelling: "Pele jovem não precisa de excesso. Eu escolho o essencial: limpar com suavidade e hidratar com intenção — sem estímulos desnecessários.",
    offers: ["Rotina minimalista (2 passos)", "Rotina completa (3 passos) com SOS"],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Hidratação localizada", sku: "BCH01" },
      step3: { name: "SOS quando necessário", sku: "BCB01" }
    },
    products: ["BCS01", "BCH01", "BCH02", "BCB01"],
    activesFocus: ["Camomila (Apigenina)", "Chá Verde (EGCG)", "Alantoína", "Ácido Hialurônico"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Rotina minimalista" },
    seo: {
      keywords: ["rotina minimalista pele sensível", "clean beauty pele sensível", "sabão líquido suave pele"],
      metaTitle: "Pele jovem sensível — rotina NAT HOF minimalista",
      metaDescription: "Rotina essencial: limpeza suave e hidratação funcional para pele sensível e reativa."
    },
    faq: [
      { q: "Preciso de muitos produtos?", a: "Não. A proposta é essencial e consistente: limpar e hidratar; o SOS entra se necessário." },
      { q: "Fica pegajoso?", a: "A proposta é toque seco e conforto. Ajuste a quantidade conforme sua pele." },
      { q: "Minha pele irrita fácil, o que priorizar?", a: "Limpeza suave e hidratação localizada." },
      { q: "Qual melhor para mãos ressecadas por lavagem?", a: "Hidratante para mãos como rotina de manutenção." },
      { q: "O quiz é diagnóstico?", a: "Não. É uma consulta guiada de rotina de cuidado." }
    ],
    warnings: [],
    utmHints: ["adolescente", "jovem", "pele sensivel", "clean beauty"]
  },
  {
    slug: "pos-sol",
    title: "Pós-sol (leve) — conforto e suporte à barreira",
    hero: {
      headline: "Depois do sol, eu devolvo calma à pele.",
      subheadline: "Rotina de suporte para conforto e barreira após exposição ao sol (sem promessas médicas).",
      bullets: [
        "Hidratação funcional para ressecamento pós-exposição",
        "Cuidado pontual em áreas desconfortáveis",
        "Limpeza suave para não agravar sensibilidade"
      ]
    },
    storytelling: "O sol conta uma história na pele. Eu respondo com calma: hidratar, proteger a barreira e aliviar desconfortos com suavidade.",
    offers: ["Rotina pós-sol sem perfume", "Kit verão saudável"],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Hidratação corporal", sku: "BCH01" },
      step3: { name: "Cuidado pontual", sku: "BCB01" }
    },
    products: ["BCS01", "BBH01", "BCB01"],
    activesFocus: ["Aloe Vera", "Alantoína", "Ácido Hialurônico", "Óleo de Rosa-Mosqueta"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Rotina pós-sol" },
    seo: {
      keywords: ["hidratante pós-sol sem perfume", "pele sensibilizada pós-sol", "alívio ressecamento pós praia"],
      metaTitle: "Pós-sol — rotina NAT HOF para conforto da pele",
      metaDescription: "Rotina de suporte pós-exposição: limpeza suave, hidratação funcional e cuidado pontual."
    },
    faq: [
      { q: "Serve para ardor e vermelhidão leves?", a: "A proposta é suporte e conforto cutâneo, com hidratação e barreira." },
      { q: "Posso usar no corpo todo?", a: "Sim, ajustando a quantidade conforme necessidade." },
      { q: "Quais áreas costumam precisar do SOS?", a: "Pontos mais expostos e ressecados." },
      { q: "Qual a rotina mínima?", a: "Hidratação + limpeza suave." },
      { q: "Substitui protetor solar?", a: "Não. É uma rotina de suporte após exposição." }
    ],
    warnings: ["Consulta guiada não substitui avaliação médica."],
    utmHints: ["pos-sol", "verao", "praia", "piscina", "queimadura leve"]
  },
  {
    slug: "menopausa-climaterio",
    title: "Menopausa e climatério — hidratação inteligente",
    hero: {
      headline: "Uma nova fase pede um novo cuidado.",
      subheadline: "Rotina de hidratação funcional para pele que muda com o tempo: conforto, viço e barreira.",
      bullets: [
        "Hidratação equilibrada e funcional",
        "Suporte ao viço e conforto",
        "Rotina consistente para ressecamento e coceira"
      ]
    },
    storytelling: "A pele muda de fase — e eu mudo com ela. Eu reforço hidratação e barreira para atravessar esse capítulo com conforto e presença.",
    offers: ["Rotina hidratação inteligente", "Constância com tamanho família"],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Hidratação funcional", sku: "BCH01" },
      step3: { name: "Cuidado pontual", sku: "BCB01" }
    },
    products: ["BCS01", "BBH01", "BCH01", "BCB01"],
    activesFocus: ["Ácido Hialurônico", "Óleo de Rosa-Mosqueta", "Alantoína", "Óleo de Girassol"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Rotina para nova fase" },
    seo: {
      keywords: ["pele menopausa ressecada", "coceira menopausa pele", "hidratação menopausa"],
      metaTitle: "Menopausa — rotina NAT HOF para pele seca e sensível",
      metaDescription: "Rotina de hidratação funcional e suporte à barreira para pele que muda na menopausa."
    },
    faq: [
      { q: "Por que a pele fica mais seca?", a: "A pele pode mudar com o tempo; a proposta é reforçar hidratação e barreira com constância." },
      { q: "Como montar rotina mínima?", a: "Limpeza suave + hidratação diária." },
      { q: "Quando usar o cuidado pontual?", a: "Em áreas críticas e ressecadas." },
      { q: "Posso usar todos os dias?", a: "Sim, ajustando conforme resposta da pele." },
      { q: "O quiz é diagnóstico?", a: "Não. É uma consulta guiada de rotina de cuidado." }
    ],
    warnings: [],
    utmHints: ["menopausa", "climaterio", "pele seca", "coceira", "50+"]
  },
  {
    slug: "pos-procedimentos",
    title: "Pós-procedimentos estéticos — suporte sem irritar",
    hero: {
      headline: "O pós é parte do resultado.",
      subheadline: "Rotina de suporte para conforto cutâneo após procedimentos, com limpeza suave e cuidado funcional.",
      bullets: [
        "Suporte à barreira sem estímulos desnecessários",
        "Rotina simples e ajustável",
        "Cuidado pontual quando necessário"
      ]
    },
    storytelling: "Pele sensibilizada pede respeito. Eu escolho uma rotina de suporte: limpar com suavidade, hidratar com intenção e evitar excessos.",
    offers: ["Rotina pós-procedimento (suporte)", "Falar com especialista para ajustar uso"],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Hidratação controlada", sku: "BCH01" },
      step3: { name: "Cuidado pontual", sku: "BCB01" }
    },
    products: ["BCS01", "BCH01", "BCB01"],
    activesFocus: ["Ácido Hialurônico", "Alantoína", "Óleo de Rosa-Mosqueta"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Rotina pós-procedimento" },
    seo: {
      keywords: ["pós-laser pele sensível", "pós-peeling hidratação", "rotina suave pós procedimento"],
      metaTitle: "Pós-procedimentos — rotina NAT HOF de suporte",
      metaDescription: "Rotina de suporte para conforto e barreira após procedimentos: limpeza suave, hidratação e cuidado pontual."
    },
    faq: [
      { q: "Posso usar logo após o procedimento?", a: "Ajuste conforme orientação do profissional responsável e a resposta da pele." },
      { q: "Qual a rotina mínima?", a: "Limpeza suave + hidratação controlada." },
      { q: "Se arder, o que faço?", a: "Reduza frequência, aplique menor quantidade e fale com especialista." },
      { q: "O intensivo é para quê?", a: "Para cuidado pontual em áreas críticas, conforme necessidade." },
      { q: "Consulta guiada substitui avaliação profissional?", a: "Não." }
    ],
    warnings: ["Consulta guiada não substitui avaliação médica/profissional."],
    utmHints: ["pos-laser", "pos-peeling", "procedimento", "estetica", "microagulhamento"]
  },
  {
    slug: "dermatites-pele-reativa",
    title: "Dermatite, alergias e pele reativa — rotina de suporte",
    hero: {
      headline: "Quando a pele reage, eu simplifico.",
      subheadline: "Rotina de suporte para conforto e barreira: limpeza suave e hidratação funcional sem perfume.",
      bullets: [
        "Rotina para quem não tolera fragrância",
        "Conforto para coceira e ressecamento",
        "Cuidado pontual para fissuras"
      ]
    },
    storytelling: "Pele reativa não precisa de excesso. Eu cuido com simplicidade clínica: limpar sem agredir e hidratar com constância.",
    offers: ["Rotina de suporte para pele reativa", "SOS fissuras e áreas críticas"],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Hidratação funcional", sku: "BCH01" },
      step3: { name: "Cuidado pontual", sku: "BCB01" }
    },
    products: ["BCS01", "BBH01", "BCH01", "BCB01"],
    activesFocus: ["Camomila (Apigenina)", "Chá Verde (EGCG)", "Alantoína", "Ácido Hialurônico", "Óleo de Girassol"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Rotina de suporte" },
    seo: {
      keywords: ["pele reativa sem perfume", "rotina dermatite suporte", "hidratação hipoalergênica", "sabonete suave"],
      metaTitle: "Pele reativa — rotina NAT HOF de suporte e conforto",
      metaDescription: "Rotina de suporte para pele reativa e sensível: limpeza suave, hidratação funcional e cuidado pontual."
    },
    faq: [
      { q: "Isso substitui tratamento médico?", a: "Não. É uma rotina de suporte e conforto cutâneo." },
      { q: "Posso usar diariamente?", a: "Sim, ajustando conforme tolerância." },
      { q: "Tenho coceira e descamação, o que priorizar?", a: "Limpeza suave e hidratação funcional; use SOS para fissuras." },
      { q: "Qual rotina mínima?", a: "Limpeza suave + hidratação." },
      { q: "Como evitar gatilhos?", a: "Simplifique a rotina e evite estímulos desnecessários; se precisar, fale com especialista." }
    ],
    warnings: ["Consulta guiada não substitui avaliação médica."],
    utmHints: ["dermatite", "alergia", "psoriase", "eczema", "pele reativa"]
  },
  {
    slug: "pos-barba-homens",
    title: "Homens com pele sensível — pós-barba sem agressão",
    hero: {
      headline: "Pós-barba não precisa arder.",
      subheadline: "Rotina para conforto após barbear: limpeza suave e cuidado pontual em microirritações.",
      bullets: [
        "Sem perfume e com foco em tolerabilidade",
        "Cuidado pontual em áreas sensibilizadas",
        "Rotina simples e objetiva"
      ]
    },
    storytelling: "Pele masculina também carrega história: rotina, pressa, atrito. Eu respondo com cuidado funcional e direto — sem excesso.",
    offers: ["Rotina pós-barba sensível", "SOS microirritações"],
    routine: {
      step1: { name: "Limpeza suave", sku: "BCS01" },
      step2: { name: "Cuidado pontual pós-barba", sku: "BCB01" },
      step3: { name: "Hidratação diária (se necessário)", sku: "BCH01" }
    },
    products: ["BCS01", "BCB01", "BCH01"],
    activesFocus: ["Alantoína", "Ácido Hialurônico", "Óleo de Rosa-Mosqueta", "Óleo de Girassol Ozonizado"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Pós-barba sem ardor" },
    seo: {
      keywords: ["pós barba pele sensível", "sem álcool sem perfume pós barba", "irritação ao barbear"],
      metaTitle: "Pós-barba sensível — rotina NAT HOF para conforto",
      metaDescription: "Rotina simples para conforto pós-barba: limpeza suave e cuidado pontual em áreas sensibilizadas."
    },
    faq: [
      { q: "Posso usar todo dia?", a: "Sim, como rotina de cuidado e conforto." },
      { q: "Isso substitui loção pós-barba?", a: "É uma alternativa de rotina de suporte sem perfume; ajuste conforme preferência e tolerância." },
      { q: "Onde aplicar o cuidado pontual?", a: "Nas áreas que ficam mais sensibilizadas após barbear." },
      { q: "Qual rotina mínima?", a: "Limpeza suave + cuidado pontual quando necessário." },
      { q: "O quiz é diagnóstico?", a: "Não." }
    ],
    warnings: [],
    utmHints: ["homem", "pos-barba", "barbear", "pele sensivel masculina"]
  },
  {
    slug: "profissionais-saude-epi",
    title: "Profissionais de saúde — mãos ressecadas por EPI e álcool gel",
    hero: {
      headline: "Para quem cuida: mãos íntegras também são ferramenta.",
      subheadline: "Rotina para mãos ressecadas por lavagem frequente e álcool em gel: conforto, barreira e manutenção.",
      bullets: [
        "Manutenção diária para reduzir ressecamento",
        "SOS fissuras e áreas críticas",
        "Rotina rápida para plantões"
      ]
    },
    storytelling: "Quem cuida dos outros precisa de cuidado também. Eu devolvo conforto às mãos com rotina simples, repetível e funcional.",
    offers: ["Rotina anti-fissura para mãos", "SOS fissuras para plantões"],
    routine: {
      step1: { name: "Hidratação de manutenção (mãos)", sku: "BCH02" },
      step2: { name: "SOS fissuras", sku: "BCB01" },
      step3: { name: "Suporte banho/limpeza", sku: "BCS01" }
    },
    products: ["BCH02", "BCB01", "BCS01"],
    activesFocus: ["Manteiga de Karité", "Própolis", "Ácido Hialurônico", "Óleo de Girassol Ozonizado"],
    ctas: { primary: "Fazer consulta guiada (2 min)", secondary: "Rotina para mãos de plantão" },
    seo: {
      keywords: ["mãos ressecadas álcool gel", "dermatite de contato profissional saúde", "fissuras mãos plantão"],
      metaTitle: "Profissionais de saúde — rotina NAT HOF para mãos ressecadas",
      metaDescription: "Rotina rápida e funcional para mãos ressecadas: hidratação de manutenção e SOS fissuras."
    },
    faq: [
      { q: "Quantas vezes ao dia aplicar?", a: "Ajuste conforme sua rotina de lavagem das mãos; constância é o principal." },
      { q: "O que usar quando a fissura aparece?", a: "Use o SOS localizado e mantenha hidratação de manutenção." },
      { q: "Serve para dermatite de contato?", a: "É uma rotina de suporte e conforto; não substitui avaliação médica." },
      { q: "Qual a rotina mínima no plantão?", a: "Hidratação de manutenção + SOS quando necessário." },
      { q: "O quiz é diagnóstico?", a: "Não." }
    ],
    warnings: ["Consulta guiada não substitui avaliação médica."],
    utmHints: ["epi", "alcool gel", "profissional saude", "enfermagem", "medico", "plantao"]
  }
];

// Default niche for when no specific niche is identified
export const defaultNiche: Niche = {
  slug: "pele-sensivel-geral",
  title: "Pele Sensível — cuidado diário",
  hero: {
    headline: "Cuidar da pele sensível é cuidar da sua história.",
    subheadline: "Rotina de suporte para pele que pede atenção: hidratação funcional, limpeza suave e conforto diário.",
    bullets: [
      "Limpeza suave que respeita a barreira",
      "Hidratação funcional com toque seco",
      "Fórmulas sem fragrância artificial"
    ]
  },
  storytelling: "Pele sensível conta uma história única. Eu cuido com ciência e afeto: limpar sem agredir, hidratar com intenção e proteger com constância.",
  offers: [
    "Consulta guiada personalizada (2 min)",
    "Rotina em 3 passos para pele sensível",
    "Kits com economia para rotina completa"
  ],
  routine: {
    step1: { name: "Limpeza suave", sku: "BCS01" },
    step2: { name: "Hidratação diária", sku: "BCH01" },
    step3: { name: "Cuidado intensivo", sku: "BCB01" }
  },
  products: ["BCS01", "BCH01", "BBH01", "BCB01"],
  activesFocus: ["Ácido Hialurônico", "Alantoína", "Aloe Vera", "Camomila (Apigenina)", "Chá Verde (EGCG)"],
  ctas: {
    primary: "Fazer consulta guiada (2 min)",
    secondary: "Ver todos os produtos"
  },
  seo: {
    keywords: ["pele sensível", "hidratante pele sensível", "rotina pele sensível", "dermocosméticos"],
    metaTitle: "NAT HOF — Dermocosméticos para Pele Sensível",
    metaDescription: "Rotina de cuidado para pele sensível: limpeza suave, hidratação funcional e suporte à barreira cutânea."
  },
  faq: [
    { q: "Os produtos são seguros para peles muito sensibilizadas?", a: "Sim. Os produtos são dermatologicamente testados pelo IPclin (HRIPT, 60 participantes) e formulados para alta tolerabilidade." },
    { q: "São veganos?", a: "Quase toda a linha. O Hidratante para as Mãos e o Hidratante para os Pés contêm extrato de própolis, de origem apícola — por isso não recebem o selo vegano. Os demais produtos são veganos. Nenhum produto NAT HOF é testado em animais." },
    { q: "Posso usar durante tratamentos?", a: "Nossos produtos são formulados para peles sensibilizadas. Consulte seu médico para orientação específica." },
    { q: "Qual a rotina mínima?", a: "Limpeza suave + hidratação diária." },
    { q: "O quiz substitui orientação médica?", a: "Não. É uma consulta guiada de rotina de cuidado." }
  ],
  warnings: ["Consulta guiada não substitui avaliação médica."],
  utmHints: ["pele sensivel", "sensivel", "geral"]
};

// Helper to get niche by slug
export const getNicheBySlug = (slug: string): Niche | null => {
  return niches.find(n => n.slug === slug) || null;
};

// Helper to get all niche slugs
export const getAllNicheSlugs = (): string[] => {
  return niches.map(n => n.slug);
};

// SKU to product info mapping
export const skuToProduct: Record<string, { id: string; name: string; size: string }> = {
  "BCS01": { id: "BCS01", name: "Sabonete Líquido Extra Suave", size: "150ml" },
  "BBH01": { id: "BBH01", name: "Hidratante Corporal", size: "1000g" },
  "BCH01": { id: "BCH01", name: "Hidratante Corporal", size: "80g" },
  "BCH02": { id: "BCH02", name: "Hidratante para Mãos", size: "80g" },
  "BCH03": { id: "BCH03", name: "Hidratante para Pés", size: "80g" },
  "BCB01": { id: "BCB01", name: "Bálsamo Hidratante Ozonizado", size: "30ml" }
};

// Niche bundle mappings (SKUs for each niche) - uses 80g (BCH01) as default for consumers
export const nicheBundles: Record<string, string[]> = {
  "onco-care": ["BCS01", "BCH01", "BCB01"],
  "barreira-melasma": ["BCS01", "BCH01", "BCB01"],
  "inverno-baixa-umidade": ["BCS01", "BCH01", "BCB01"],
  "viagem-neve-altitude": ["BCS01", "BCH01", "BCB01"],
  "maternidade-segura": ["BCS01", "BCH01", "BCB01"],
  "pele-jovem-sensivel": ["BCS01", "BCH01"],
  "pos-sol": ["BCS01", "BCH01", "BCB01"],
  "menopausa-climaterio": ["BCS01", "BCH01", "BCB01"],
  "pos-procedimentos": ["BCS01", "BCH01", "BCB01"],
  "dermatites-pele-reativa": ["BCS01", "BCH01", "BCB01"],
  "pos-barba-homens": ["BCS01", "BCB01"],
  "profissionais-saude-epi": ["BCH02", "BCB01", "BCS01"],
  "pele-sensivel-geral": ["BCS01", "BCH01", "BCB01"]
};
